// whitelabelSignin.js — the persona sign-in buttons' action, WHITE-LABEL
// HOSTS ONLY (WHITELABEL-BRANDED-BOOT-1, tickets/opus.md).
//
// Nika asked for a branded login with "pre-made user sign-ins" — Admin /
// Searcher / Investor buttons that sign in as a pre-provisioned user.
//
// ⚠️ Registered ONLY behind `isCustomDomainHost()` (context.js), same gate as
// the branded components, per Nika 2026-08-19: "do not change anything for
// symbols — this is for bellforge workspace gate only."
//
// ── The client never names a user ────────────────────────────────────────
// It sends a persona KEY ('admin' | 'searcher' | …) and the HOST. The server
// maps key → pre-provisioned user from the org's own record and refuses
// unless every gate holds (non-production channel, org flag on, verified
// custom domain, known key). Nothing here can select an arbitrary account,
// which is the same invariant the PersonaSession model states for role
// simulation: no client-supplied field may name a target user.
//
// ── No invented auth path ────────────────────────────────────────────────
// The response is the `/demo/start` envelope, and the session is handed to
// the SDK through ITS OWN documented storage contract
// (`TokenManager.storageKeys`) plus a full reload — the SDK's hydration path,
// not a second auth scheme. See the note at the persistence step for why
// this deliberately avoids `sdk._tokenManager` even though it now resolves.

/**
 * Sign in as one of the org's pre-provisioned personas.
 *
 * Called from the branded login as
 * `el.call('whitelabelPersonaSignin', personaKey)`.
 *
 * Resolves `{ ok: true }` after kicking a full-page load, or
 * `{ ok: false, message }` — never throws, so a refused persona leaves the
 * login usable instead of stranding it.
 */
export const whitelabelPersonaSignin = async function (persona, opts = {}) {
  const el = this
  const win = opts.win || (typeof window !== 'undefined' ? window : null)
  const key = typeof persona === 'string' ? persona.trim() : ''
  if (!key || !win || !win.location)
    return { ok: false, message: el.call('polyglot', 'auth.whitelabel.unavailable') }

  const apiBase = String(
    opts.apiBase || (el?.call ? el.call('workspaceApiBase') : '') || ''
  ).replace(/\/+$/, '')
  const fetchImpl = opts.fetchImpl || (typeof win.fetch === 'function' ? win.fetch.bind(win) : null)
  if (!apiBase || typeof fetchImpl !== 'function') {
    return { ok: false, message: el.call('polyglot', 'auth.whitelabel.unavailable') }
  }

  let data = null
  try {
    const res = await fetchImpl(`${apiBase}/core/organizations/whitelabel-signin`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      // No credentials: this mints a NEW session; it must not depend on,
      // or send, whatever session the browser already carries.
      credentials: 'omit',
      body: JSON.stringify({ host: win.location.hostname, persona: key })
    })
    const body = await res.json().catch(() => null)
    if (!res.ok || !body || !body.success || !body.data) {
      // The server refuses uniformly and without enumerating — mirror that
      // here rather than surfacing a reason the server deliberately withheld.
      return { ok: false, message: el.call('polyglot', 'auth.whitelabel.signinUnavailable') }
    }
    data = body.data
  } catch (_) {
    return { ok: false, message: el.call('polyglot', 'auth.whitelabel.signinUnavailable') }
  }

  const accessToken = data.accessToken
  const refreshToken = data.refreshToken
  if (!accessToken)
    return { ok: false, message: el.call('polyglot', 'auth.whitelabel.signinUnavailable') }

  // ── Persisting the session ───────────────────────────────────────────
  // `sdk._tokenManager` now resolves on the SDK root (assigned at the end
  // of `initialize()`, sdk/src/index.js) as of sdk@f73e6c8
  // (SDK-TOKENMANAGER-ROOT-1; see sdk/src/services/tests/SDK/
  // tokenManager-root.test.js) — it used to be a field that only existed
  // per-service (BaseService.js), so the root read undefined and every
  // reader of it, including this handler's earlier version, silently
  // failed. `magic-callback.js` and `packages/meet/functions/
  // mintLivekitToken.js` read the same root field and are expected to work
  // now too. The `invalidateClaims` guards this file used to warn about
  // are gone as of sdk@e211dc6 (SDK-INVALIDATECLAIMS-DEAD-GUARD-1) — there
  // was never a claim cache to invalidate.
  //
  // This handler still avoids `sdk._tokenManager` on purpose: going
  // through the SDK's OWN storage contract instead (`TokenManager.
  // storageKeys`, prefix `symbols_`, no consumer overrides it) lets the
  // reload below rebuild the session via the SDK's documented hydration
  // path, rather than reaching into a service-owned singleton directly.
  // Verified end to end on the deployed host. `expires_at` is epoch ms,
  // matching what `saveTokens()` writes and what the server sends as
  // `accessExp`.
  try {
    // Off `win`, not the global: identical object in a browser, and it keeps
    // the storage seam injectable like `win`/`fetchImpl` already are — so a
    // test can assert the REAL keys instead of stubbing an SDK internal.
    const store = opts.storage || win.localStorage || null
    if (!store) return { ok: false, message: el.call('polyglot', 'auth.whitelabel.unavailable') }
    store.setItem('symbols_access_token', accessToken)
    if (refreshToken) {
      store.setItem('symbols_refresh_token', refreshToken)
      // A sign-in on this origin: its refresh token is its own (review D1).
      store.setItem('symbols_session_source', 'own')
    }
    const accessExp = data.accessExp ? Number(data.accessExp) : null
    if (accessExp) {
      store.setItem('symbols_expires_at', String(accessExp))
      store.setItem(
        'symbols_expires_in',
        String(Math.max(0, Math.floor((accessExp - Date.now()) / 1000)))
      )
    }
  } catch (_) {
    return { ok: false, message: el.call('polyglot', 'auth.whitelabel.unavailable') }
  }

  // Full-page load, not a router push — the same deliberate choice
  // `pages/demo.js` makes after `startDemo`: boot must re-run from scratch so
  // every scope fetch, the workspace resolve and the branded loader all see
  // the new session, instead of a half-rescoped SPA.
  try {
    win.location.assign('/')
  } catch (_) {}
  return { ok: true }
}
